import React, { useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import InputMask from 'react-input-mask';
import { Modal, ModalTitle, ModalBody, ModalFooter } from '../components/Modal';
import CardGenerator from '../components/CardGenerator';
import {
  LuUser,
  LuPhone,
  LuCalendar,
  LuPencil,
  LuShieldCheck,
  LuCreditCard,
  LuFileText,
  LuCircleCheck,
  LuTriangleAlert,
  LuUsers,
  LuIdCard,
  LuLoaderCircle,
  LuCircleHelp,
  LuBell,
  LuBellOff,
  LuBellRing
} from 'react-icons/lu';

import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { requisicaoAPI } from '../services/api';
import { formatters } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import ErroLoadData from '../components/ErroLoadData';
import { downloadBlob } from '../utils/downloadUtils';
import { cartoesAPI } from '../services/api';
import cidadesEstados from '../utils/cidades-estados.json';
import { formatSituacaoContrato, resolveSituacaoRaw, isSituacaoEmDia } from '../utils/situacaoContrato';
import {
  isWebPushConfigured,
  syncPushDeviceWithBackend,
  disablePushDeviceOnBackend,
  isPushEnabledOnThisBrowser,
  getNotificationPermission,
  getStoredFcmToken,
  setPushPromptStatus,
  requestNotificationPermissionNow,
} from '../services/firebaseMessaging';

const ProfileContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 24px;
  
  @media (max-width: 768px) {
    padding: 16px 12px 24px;
  }
`;

const ProfileHeader = styled.div`
  text-align: center;
  margin-bottom: 32px;

  @media (max-width: 768px) {
    margin-bottom: 20px;
  }
  
  h1 {
    color: ${props => props.theme.colors.primary};
    font-size: 32px;
    font-weight: 700;
    margin-bottom: 8px;
    background: linear-gradient(135deg, ${props => props.theme.colors.primary}, ${props => props.theme.colors.secondary});
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    
    @media (max-width: 768px) {
      font-size: 24px;
    }
  }
  
  p {
    color: ${props => props.theme.colors.textSecondary};
    font-size: 16px;
    font-weight: 500;

    @media (max-width: 768px) {
      font-size: 15px;
    }
  }
`;

const ProfileGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 24px;
  margin-bottom: 32px;
  
  @media (max-width: 1024px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const ProfileCard = styled(motion.div)`
  background: ${props => props.theme.colors.white};
  border-radius: 16px;
  padding: 24px;
  box-shadow: ${props => props.theme.shadows.card};
  border: 1px solid ${props => props.theme.colors.border};
  transition: all 0.3s ease;
  min-width: 0;
  max-width: 100%;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${props => props.theme.shadows.lg};
  }

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 2px solid ${props => props.theme.colors.borderLight};
  min-width: 0;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const CardTitle = styled.h3`
  color: ${props => props.theme.colors.primary};
  font-size: 18px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  margin: 0;
`;

const EditButton = styled.button`
  background: ${props => props.theme.colors.secondary};
  color: ${props => props.theme.colors.white};
  border: none;
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 6px;
  
  &:hover {
    background: ${props => props.theme.colors.primary};
    transform: translateY(-1px);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const ModalButton = styled.button`
  border: none;
  padding: 12px 24px;
  border-radius: 10px;
  cursor: pointer;
  font-size: 15px;
  font-weight: 700;
  transition: all 0.3s ease;
  min-width: 140px;

  &.confirm {
    background: ${({ theme }) => theme.colors.gradientPrimary};
    color: ${({ theme }) => theme.colors.white};
    box-shadow: 0 10px 15px -3px rgba(30, 64, 175, 0.25);
    
    &:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 15px 20px -3px rgba(30, 64, 175, 0.35);
    }
  }

  &.cancel {
    background: #f1f5f9;
    color: #64748b;
    &:hover:not(:disabled) {
      background: #e2e8f0;
      color: #1e293b;
    }
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
  display: flex;
  flex-direction: column;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0;
  }
`;

const FormSectionDivider = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 8px 0 20px;
  color: ${props => props.theme.colors.textSecondary};
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: ${props => props.theme.colors.border || '#e2e8f0'};
  }
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const InfoLabel = styled.label`
  color: ${props => props.theme.colors.textSecondary};
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const InfoValue = styled.div`
  color: ${props => props.theme.colors.textPrimary};
  font-size: 14px;
  font-weight: 500;
  padding: 8px 12px;
  background: ${props => props.theme.colors.grayLight};
  border-radius: 6px;
  border: 1px solid ${props => props.theme.colors.border};
  overflow-wrap: anywhere;
  word-break: break-word;
`;

const ReadonlyValue = styled.div`
  color: #64748b;
  font-size: 14px;
  font-weight: 500;
  padding: 12px 16px;
  background: #f1f5f9;
  border-radius: 10px;
  border: 1px dashed #cbd5e1;
  min-height: 46px;
  display: flex;
  align-items: center;
`;

const FieldHint = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  color: #94a3b8;
  cursor: help;

  &:hover,
  &:focus-within {
    color: ${props => props.theme.colors.secondary || '#f59e0b'};
  }

  &::after {
    content: attr(data-tooltip);
    position: absolute;
    left: 50%;
    bottom: calc(100% + 8px);
    transform: translateX(-50%);
    width: max-content;
    max-width: 220px;
    padding: 8px 10px;
    border-radius: 8px;
    background: #0f172a;
    color: #fff;
    font-size: 11px;
    font-weight: 500;
    line-height: 1.35;
    text-transform: none;
    letter-spacing: 0;
    white-space: normal;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.15s ease;
    z-index: 20;
    box-shadow: 0 8px 20px rgba(15, 23, 42, 0.2);
  }

  &::before {
    content: '';
    position: absolute;
    left: 50%;
    bottom: calc(100% + 2px);
    transform: translateX(-50%);
    border: 5px solid transparent;
    border-top-color: #0f172a;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.15s ease;
    z-index: 20;
  }

  &:hover::after,
  &:hover::before,
  &:focus-within::after,
  &:focus-within::before {
    opacity: 1;
  }
`;

const FieldHelperText = styled.span`
  color: #64748b;
  font-size: 12px;
  margin-top: 6px;
  display: block;
  line-height: 1.4;
`;

const ContactHelperText = styled(FieldHelperText)`
  text-align: center;
  margin-top: 2px;
  margin-bottom: 16px;
`;

const CheckboxRow = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
  color: ${props => props.theme.colors.textSecondary};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  user-select: none;

  input {
    width: 16px;
    height: 16px;
    accent-color: ${props => props.theme.colors.secondary};
    cursor: pointer;
  }
`;

const NotificationOptIn = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-top: 8px;
  padding: 12px 14px;
  background: #f8fafc;
  border: 1px solid ${props => props.theme.colors.borderLight || '#e2e8f0'};
  border-radius: 10px;
  color: ${props => props.theme.colors.textSecondary};
  font-size: 13px;
  font-weight: 500;
  line-height: 1.4;
  cursor: pointer;
  user-select: none;

  input {
    width: 16px;
    height: 16px;
    margin-top: 2px;
    flex-shrink: 0;
    accent-color: ${props => props.theme.colors.secondary};
    cursor: pointer;
  }

  span {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  small {
    font-size: 11px;
    font-weight: 400;
    color: #94a3b8;
  }
`;

const PushCard = styled(ProfileCard)`
  grid-column: 1 / -1;
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
`;

const PushBody = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  flex-wrap: wrap;
  min-width: 0;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }
`;

const PushInfo = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  flex: 1;
  min-width: 0;
`;

const PushCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`;

const PushStatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0;
  text-transform: none;
  background: ${props => props.$bg || '#f1f5f9'};
  color: ${props => props.$color || '#64748b'};
  border: 1px solid ${props => props.$border || 'transparent'};
  max-width: 100%;
  white-space: nowrap;

  @media (max-width: 768px) {
    white-space: normal;
  }
`;

const PushDescription = styled.p`
  margin: 0;
  font-size: 13px;
  line-height: 1.45;
  color: ${props => props.theme.colors.textSecondary};
  max-width: 520px;
`;

const PushHelp = styled.p`
  margin: 0;
  font-size: 12px;
  line-height: 1.4;
  color: #b45309;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 8px;
  padding: 8px 10px;
`;

const PushActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 1;
  min-width: 0;
  max-width: 100%;

  button {
    white-space: normal;
    text-align: center;
    max-width: 100%;
  }

  @media (max-width: 768px) {
    width: 100%;

    button {
      width: 100%;
      justify-content: center;
    }
  }
`;

const PushPrimaryButton = styled(EditButton)`
  background: ${props => props.theme.colors.gradientPrimary || props.theme.colors.primary};
  box-shadow: 0 8px 16px -6px rgba(30, 64, 175, 0.35);
  padding: 10px 18px;

  &:hover:not(:disabled) {
    background: ${props => props.theme.colors.primary};
  }

  .spinner { animation: pushSpin 1s linear infinite; }
  @keyframes pushSpin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
`;

const PushSecondaryButton = styled(EditButton)`
  background: #fff;
  color: #64748b;
  border: 1px solid #e2e8f0;
  box-shadow: none;

  &:hover:not(:disabled) {
    background: #fef2f2;
    color: #b91c1c;
    border-color: #fecaca;
    transform: translateY(-1px);
  }

  .spinner { animation: pushSpin 1s linear infinite; }
`;

const EditableInput = styled.input`
  color: ${props => props.theme.colors.textPrimary};
  font-size: 15px;
  font-weight: 500;
  padding: 12px 16px;
  background: #ffffff;
  border: 1.5px solid #cbd5e1;
  border-radius: 10px;
  width: 100%;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.secondary};
    background: ${props => props.theme.colors.white};
    box-shadow: 0 0 0 4px rgba(245, 158, 11, 0.1);
  }

  &::placeholder {
    color: #94a3b8;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    background: #f8fafc;
  }
`;

const OtpHintBox = styled.div`
  text-align: center;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 20px;
  color: ${props => props.theme.colors.textSecondary};
  font-size: 14px;
  line-height: 1.5;

  strong {
    color: ${props => props.theme.colors.textPrimary};
  }
`;

const OtpInput = styled(EditableInput)`
  text-align: center;
  letter-spacing: 8px;
  font-size: 22px;
  font-weight: 700;
`;

const LinkButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.colors.secondary};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
  margin-top: 12px;
  display: inline-flex;
  align-items: center;
  gap: 8px;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CooldownBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 42px;
  padding: 2px 8px;
  border-radius: 999px;
  background: #f1f5f9;
  color: #64748b;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.2px;
`;

const StatusSection = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 16px;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const StatusCard = styled.div`
  background: ${props => props.theme.colors.white};
  border-radius: 16px;
  padding: 20px;
  box-shadow: ${props => props.theme.shadows.card};
  border: 1px solid ${props => props.theme.colors.border};
  display: flex;
  align-items: center;
  gap: 16px;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${props => props.theme.shadows.lg};
  }

  @media (max-width: 992px) {
    &:nth-child(3) {
      grid-column: 1 / -1;
    }
  }

  @media (max-width: 768px) {
    padding: 16px;
    gap: 12px;
  }
`;

const StatusIcon = styled.div`
  width: 52px;
  height: 52px;
  border-radius: 12px;
  background: ${props => props.bgColor || props.theme.colors.lightBlue};
  color: ${props => props.color || props.theme.colors.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 44px;
    height: 44px;
    font-size: 20px;
    border-radius: 10px;
  }
`;

const StatusContent = styled.div`
  flex: 1;
`;

const StatusTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${props => props.theme.colors.textSecondary};
  margin-bottom: 2px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const StatusValue = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: ${props => props.theme.colors.primary};

  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const ContractSection = styled.div`
  background: ${props => props.theme.colors.white};
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.03);
  border: 1px solid #e2e8f0;
  border-left: 5px solid #1e3a8a2e;
  margin-bottom: 32px;
  position: relative;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.1);
    border-color: #1e3a8a;
    border-left-color: #1e3a8a;
  }

  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const ContractHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 20px;
  margin-right: 80px; // Espaço para o status
`;

const ContractAvatar = styled.div`
  width: 52px;
  height: 52px;
  border-radius: 12px;
  background: #dbeafe;
  color: #1e3a8a;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  flex-shrink: 0;
`;

const ContractTitle = styled.div`
  display: flex;
  flex-direction: column;
  
  h3 {
    color: ${props => props.theme.colors.primary};
    font-size: 18px;
    font-weight: 700;
    margin: 0;
  }
  
  span {
    font-size: 12px;
    color: ${props => props.theme.colors.grayMedium};
    font-weight: 600;
  }
`;

const ContractStatusWrapper = styled.div`
  position: absolute;
  top: 24px;
  right: 24px;
`;

const ContractInfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  padding: 20px 0;
  border-top: 1px solid #f1f5f9;
  border-bottom: 1px solid #f1f5f9;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const ContractItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ContractLabel = styled.div`
  color: ${props => props.theme.colors.grayMedium};
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ContractValue = styled.div`
  color: ${props => props.theme.colors.textPrimary};
  font-size: 15px;
  font-weight: 700;
`;

const ErrorMessage = styled.p`
  color: ${props => props.theme.colors.error};
  font-size: 12px;
  text-align: center;
  width: 100%;
  margin-top: -10px;
  margin-bottom: 15px;
`;

const InlineErrorMessage = styled.span`
  color: ${props => props.theme.colors.error};
  font-size: 12px;
  margin-top: 4px;
`;

const DependentsListContainer = styled(ProfileCard)`
  grid-column: 1 / -1;
`;

const DependentItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid ${props => props.theme.colors.borderLight};

  &:last-child {
    border-bottom: none;
  }
`;

const DependentName = styled.span`
  font-weight: 500;
  color: ${props => props.theme.colors.textPrimary};
`;

const ActionButton = styled.button`
  background: transparent;
  border: 1px solid #e2e8f0;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: ${props => props.theme.colors.secondary};
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    background: ${props => props.theme.colors.secondary};
    color: white;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  .spinner { animation: spin 1s linear infinite; }
  @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
`;

const formatResponsavelEndereco = (responsavel) => {
  if (!responsavel) return null;
  const streetParts = [responsavel.endereco, responsavel.numero].filter(Boolean).join(', ');
  const mid = [streetParts, responsavel.complemento, responsavel.bairro].filter(Boolean).join(' - ');
  const cityState = [responsavel.cidade, responsavel.estado].filter(Boolean).join(' - ');
  let result = [mid, cityState].filter(Boolean).join(', ');
  if (responsavel.cep) {
    result = result ? `${result}, CEP: ${responsavel.cep}` : `CEP: ${responsavel.cep}`;
  }
  if (responsavel.referencia) {
    result = result
      ? `${result} — Ref.: ${responsavel.referencia}`
      : `Ref.: ${responsavel.referencia}`;
  }
  return result || null;
};

const formatEstadoLabel = (sigla) => {
  if (!sigla) return 'Preenchido pelo CEP';
  const uf = cidadesEstados.estados.find((e) => e.sigla === sigla);
  return uf ? `${uf.sigla} — ${uf.nome}` : sigla;
};

const isSemNumeroValue = (value) => {
  const normalized = String(value || '').trim().toUpperCase().replace(/\s+/g, '');
  return ['S/N', 'SN', 'S.N', 'S.N.', 'SEMNUMERO', 'SEMNÚMERO'].includes(normalized);
};

const parseAceitaNotificacao = (value) => {
  if (value === true || value === 1) return true;
  if (value === false || value === 0 || value == null || value === '') return false;
  const normalized = String(value).trim().toUpperCase();
  return ['S', 'SIM', 'TRUE', '1', 'YES'].includes(normalized);
};

const normalizeComparableValue = (key, value) => {
  if (key === 'aceita_notificacao') return parseAceitaNotificacao(value);
  const raw = value == null ? '' : String(value).trim();
  if (key === 'telefone' || key === 'cep') return raw.replace(/\D/g, '');
  if (key === 'email') return raw.toLowerCase();
  if (key === 'estado') return raw.toUpperCase();
  if (key === 'numero') {
    if (isSemNumeroValue(raw)) return 'S/N';
    return raw;
  }
  return raw;
};

const resolvePerfilTelefone = (responsavel, cliente) => (
  responsavel?.celular
  || responsavel?.telefoneResidencial
  || responsavel?.telefone
  || cliente?.telefone
  || cliente?.celular
  || ''
);

const resolvePerfilEmail = (responsavel, cliente) => (
  responsavel?.email
  || cliente?.email
  || ''
);

const buildOriginalCadastroSnapshot = (responsavel, telefone, email) => {
  const numeroAtual = responsavel?.numero || '';
  const telefoneFormatado = formatters.formatPhone(telefone) || telefone || '';
  return {
    telefone: telefoneFormatado,
    email: email || '',
    endereco: responsavel?.endereco || '',
    numero: isSemNumeroValue(numeroAtual) ? 'S/N' : numeroAtual,
    complemento: responsavel?.complemento || '',
    referencia: responsavel?.referencia || '',
    bairro: responsavel?.bairro || '',
    cidade: responsavel?.cidade || '',
    estado: responsavel?.estado || '',
    cep: responsavel?.cep || '',
    aceita_notificacao: parseAceitaNotificacao(
      responsavel?.aceita_notificacao ?? responsavel?.aceitaNotificacao
    )
  };
};

const hasCadastroChanges = (original, current) => {
  if (!original || !current) return true;
  const keys = [
    'telefone', 'email', 'endereco', 'numero', 'complemento',
    'referencia', 'bairro', 'cidade', 'estado', 'cep', 'aceita_notificacao'
  ];
  return keys.some((key) => {
    const currentValue = key === 'numero' && current.semNumero
      ? 'S/N'
      : current[key];
    return normalizeComparableValue(key, original[key]) !== normalizeComparableValue(key, currentValue);
  });
};

const Profile = () => {
  const { user, loading: authLoading, selectedContrato, contratos } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalStep, setModalStep] = useState('form'); // 'form' | 'codigo'
  const [formData, setFormData] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCepLoading, setIsCepLoading] = useState(false);
  const [generatingId, setGeneratingId] = useState(null);
  const [otpCode, setOtpCode] = useState('');
  const [otpDestinos, setOtpDestinos] = useState({ emailMascarado: null, telefoneMascarado: null });
  const [pendingPayload, setPendingPayload] = useState(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [codigoJaEnviado, setCodigoJaEnviado] = useState(false);
  const [originalFormData, setOriginalFormData] = useState(null);
  const [pushEnabling, setPushEnabling] = useState(false);
  const [pushDisabling, setPushDisabling] = useState(false);
  const [pushEnabled, setPushEnabled] = useState(() => isPushEnabledOnThisBrowser());

  useEffect(() => {
    setPushEnabled(isPushEnabledOnThisBrowser());
  }, []);

  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const timer = setInterval(() => {
      setResendCooldown((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  const { data, isLoading, error, refetch } = useQuery(
    ['dashboard', user?.documento, selectedContrato.numero],
    async () => {
      const response = await api.get('/cliente/dashboard');
      return response.data.data;
    },
    {
      enabled: !!user?.documento,
      staleTime: 2 * 60 * 1000,
      onError: () => toast.error('Erro ao carregar dados do perfil')
    }
  );

  const cliente = data?.cliente;
  const responsavel = data?.responsavel;
  const contrato = data?.contrato;
  const resumo = data?.resumo;
  const pagamentos = data?.pagamentos;
  const dependentes = data?.dependentes || [];

  const perfilNome = responsavel?.nome || cliente?.nome;
  const perfilSexo = responsavel?.sexo || cliente?.sexo;
  const perfilTelefone = resolvePerfilTelefone(responsavel, cliente);
  const perfilEmail = resolvePerfilEmail(responsavel, cliente);
  const perfilEndereco = formatResponsavelEndereco(responsavel) || cliente?.endereco;
  const perfilAceitaNotificacao = parseAceitaNotificacao(
    responsavel?.aceita_notificacao ?? responsavel?.aceitaNotificacao
  );

  const todosMembros = [
    ...(cliente?.nome ? [{ nome: cliente.nome }] : []),
    ...dependentes
      .filter(d => d?.nome && d.status === 'ATIVO')
      .map(d => ({ nome: d.nome })),
  ];

  const handleGenerateSingleCard = async (membro) => {
    if (!membro || !contrato?.numero) {
      toast.error("Dados insuficientes para gerar o cartão.");
      return;
    }
    setGeneratingId(membro.nome);
    try {
      toast.info('Gerando cartão, por favor aguarde...');
      const response = await cartoesAPI.gerar([membro], contrato.numero);

      // Criar blob do PDF
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const filename = `cartao-${membro.nome.replace(/\s+/g, '_')}-${contrato.numero}.pdf`;

      // Usar função utilitária compatível com iOS
      await downloadBlob(blob, filename);

      toast.success('Cartão gerado com sucesso!');
    } catch (error) {
      console.error("Falha na geração do cartão individual:", error);
      const errorMessage = error.response?.data?.message || error.message || 'Erro ao gerar cartão';
      toast.error(`Erro ao gerar cartão: ${errorMessage}`);
    } finally {
      setGeneratingId(null);
    }
  };

  const getStatusFromAtivo = (ativo) => {
    return ativo === 1 ? 'ATIVO' : 'INATIVO';
  };

  const getCurrentMonthPaymentValue = (paymentsList) => {
    if (!paymentsList || paymentsList.length === 0) return null;
    const now = new Date();
    const found = paymentsList.find((p) => {
      if (!p?.vencimento) return false;
      const parts = p.vencimento.split('/');
      if (parts.length !== 3) return false;
      const d = new Date(parseInt(parts[2], 10), parseInt(parts[1], 10) - 1, parseInt(parts[0], 10));
      return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    });
    return found ? found.valor : null;
  };

  const currentMonthValue = getCurrentMonthPaymentValue(pagamentos);

  const openEditModal = () => {
    const snapshot = buildOriginalCadastroSnapshot(responsavel, perfilTelefone, perfilEmail);
    setOriginalFormData(snapshot);
    setFormData({
      ...snapshot,
      semNumero: isSemNumeroValue(snapshot.numero)
    });
    setFormErrors({});
    setIsCepLoading(false);
    setModalStep('form');
    setOtpCode('');
    setOtpDestinos({ emailMascarado: null, telefoneMascarado: null });
    setPendingPayload(null);
    setResendCooldown(0);
    setCodigoJaEnviado(false);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setIsCepLoading(false);
    setModalStep('form');
    setOtpCode('');
    setPendingPayload(null);
    setResendCooldown(0);
    setCodigoJaEnviado(false);
    setOriginalFormData(null);
  };

  const limparCamposEndereco = (cepValue) => {
    setFormData(prev => ({
      ...prev,
      endereco: '',
      bairro: '',
      cidade: '',
      estado: '',
      numero: '',
      complemento: '',
      semNumero: false,
      ...(cepValue !== undefined ? { cep: cepValue } : {})
    }));
  };

  const buscarEnderecoPorCep = async (cepValue) => {
    const digits = (cepValue || '').replace(/\D/g, '');
    if (digits.length !== 8) return;

    setIsCepLoading(true);
    try {
      const response = await fetch(`https://viacep.com.br/ws/${digits}/json/`);
      const data = await response.json();

      if (data.erro) {
        limparCamposEndereco(cepValue);
        toast.warn('CEP não encontrado. Preencha o endereço manualmente.');
        return;
      }

      setFormData(prev => ({
        ...prev,
        endereco: data.logradouro || '',
        bairro: data.bairro || '',
        cidade: data.localidade || '',
        estado: data.uf || '',
        cep: cepValue,
        numero: '',
        complemento: '',
        semNumero: false
      }));
    } catch (error) {
      console.error('Erro ao buscar CEP:', error);
      limparCamposEndereco(cepValue);
      toast.error('Erro ao buscar o CEP. Preencha o endereço manualmente.');
    } finally {
      setIsCepLoading(false);
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
      ...(name === 'numero' ? { semNumero: false } : {}),
      ...(name === 'cep' ? { numero: '', complemento: '', semNumero: false } : {})
    }));
    if (formErrors[name] || formErrors.contato) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        delete newErrors.contato;
        return newErrors;
      });
    }
    if (name === 'cep' && value.replace(/\D/g, '').length === 8) {
      buscarEnderecoPorCep(value);
    }
  };

  const handleSemNumeroChange = (e) => {
    const checked = e.target.checked;
    setFormData(prev => ({
      ...prev,
      semNumero: checked,
      numero: checked ? 'S/N' : ''
    }));
  };

  const handleAceitaNotificacaoChange = (e) => {
    setFormData(prev => ({
      ...prev,
      aceita_notificacao: e.target.checked
    }));
  };

  const handleEnableWebPush = async () => {
    setPushEnabling(true);
    try {
      // Sem await antes — mantém o gesto do clique para o Chrome
      const permission = await requestNotificationPermissionNow();
      if (permission !== 'granted') {
        setPushPromptStatus('dismissed');
        setPushEnabled(false);
        toast.warn(
          permission === 'denied'
            ? 'O navegador bloqueou as notificações. Clique no cadeado ao lado da URL e permita notificações.'
            : 'Não foi possível obter permissão de notificação.'
        );
        return;
      }

      const result = await syncPushDeviceWithBackend({
        contrato: selectedContrato?.numero,
        permission: 'granted',
      });
      if (result.ok) {
        setPushEnabled(true);
        setPushPromptStatus('accepted');
        toast.success('Notificações do navegador ativadas.');
      } else if (result.reason === 'missing_config') {
        toast.warn('Firebase ainda não está configurado neste ambiente.');
      } else if (result.reason === 'unsupported_browser') {
        toast.warn('Este navegador não suporta Web Push.');
      } else if (result.reason === 'sync_failed') {
        toast.error('Token gerado, mas falhou ao registrar no servidor.');
      } else {
        toast.error('Não foi possível ativar as notificações.');
      }
    } catch (err) {
      console.error('Falha ao ativar Web Push:', err);
      toast.error('Erro ao ativar notificações do navegador.');
    } finally {
      setPushEnabling(false);
    }
  };

  const handleDisableWebPush = async () => {
    setPushDisabling(true);
    try {
      const result = await disablePushDeviceOnBackend(getStoredFcmToken());
      setPushEnabled(false);
      if (result.ok) {
        toast.info('Notificações desativadas neste dispositivo.');
      } else {
        toast.warn(
          'Desativadas neste navegador. Se o servidor não atualizou, tente novamente em instantes.'
        );
      }
    } catch (err) {
      console.error('Falha ao desativar Web Push:', err);
      toast.error('Erro ao desativar notificações.');
    } finally {
      setPushDisabling(false);
    }
  };

  const validateForm = () => {
    const errors = {};
    const phoneDigits = (formData.telefone || '').replace(/\D/g, '');
    const email = formData.email || '';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!phoneDigits) {
      errors.telefone = 'Informe o telefone para receber o código de confirmação.';
    } else if (phoneDigits.length < 10) {
      errors.telefone = 'Telefone incompleto. Preencha DDD + número.';
    }

    if (!email) {
      errors.email = 'Informe o e-mail para receber o código de confirmação.';
    } else if (!emailRegex.test(email)) {
      errors.email = 'O formato do e-mail é inválido.';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const buildSolicitacaoPayload = () => {
    const { semNumero, ...dados } = formData;
    return {
      tipo: 'ATUALIZACAO_CADASTRAL',
      dados: {
        ...dados,
        numero: semNumero ? 'S/N' : dados.numero,
        aceita_notificacao: parseAceitaNotificacao(dados.aceita_notificacao),
        ...(responsavel?.tipo != null && String(responsavel.tipo).trim() !== ''
          ? { tipo: responsavel.tipo }
          : {})
      }
    };
  };

  const getApiErrorMessage = (err, fallback) => {
    return err?.response?.data?.message || err?.message || fallback;
  };

  const startResendCooldown = (secondsFromApi) => {
    const seconds = Number(secondsFromApi) > 0 ? Number(secondsFromApi) : 60;
    setResendCooldown(seconds);
  };

  const handleSubmitRequest = async () => {
    if (!validateForm()) {
      toast.warn('Por favor, corrija os erros no formulário.');
      return;
    }

    if (!hasCadastroChanges(originalFormData, formData)) {
      toast.info('Nenhuma mudança encontrada nos dados.');
      return;
    }

    // Já enviou código e ainda está no cooldown: volta à etapa sem reenviar
    if (codigoJaEnviado && resendCooldown > 0) {
      setModalStep('codigo');
      toast.info(`Aguarde ${resendCooldown}s para solicitar um novo código.`);
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = buildSolicitacaoPayload();
      const response = await requisicaoAPI.solicitarAutomatica(payload);
      setPendingPayload(payload);
      setOtpDestinos(response?.data?.destinos || {});
      setOtpCode('');
      setCodigoJaEnviado(true);
      startResendCooldown(response?.data?.cooldownSeconds);
      setModalStep('codigo');
      toast.success('Código enviado para os contatos informados.');
    } catch (err) {
      const status = err?.response?.status;
      const reason = err?.response?.data?.reason;
      const retryAfter = err?.response?.data?.retryAfterSeconds;

      if (status === 429 && reason === 'cooldown') {
        if (retryAfter) startResendCooldown(retryAfter);
        setCodigoJaEnviado(true);
        setPendingPayload(buildSolicitacaoPayload());
        setModalStep('codigo');
        toast.warn(getApiErrorMessage(err, 'Aguarde para solicitar um novo código.'));
      } else if (status === 429) {
        toast.error(getApiErrorMessage(err, 'Limite de envios atingido.'));
      } else {
        toast.error(getApiErrorMessage(err, 'Erro ao enviar o código. Tente novamente.'));
      }
      console.error('Erro ao solicitar código de alteração:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmCode = async () => {
    const codigo = String(otpCode || '').replace(/\D/g, '');
    if (codigo.length !== 6) {
      toast.warn('Informe o código de 6 dígitos.');
      return;
    }

    setIsSubmitting(true);
    try {
      await requisicaoAPI.confirmarAutomatica({ codigo });
      toast.success('Alteração cadastral confirmada com sucesso!');
      closeModal();
      await refetch();
    } catch (err) {
      const status = err?.response?.status;
      if (status === 429) {
        toast.error(getApiErrorMessage(err, 'Limite de alterações por hora atingido.'));
      } else {
        toast.error(getApiErrorMessage(err, 'Código inválido ou expirado.'));
      }
      console.error('Erro ao confirmar código de alteração:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendCode = async () => {
    if (!pendingPayload) return;
    if (resendCooldown > 0) {
      toast.info(`Aguarde ${resendCooldown}s para reenviar o código.`);
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await requisicaoAPI.solicitarAutomatica(pendingPayload);
      setOtpDestinos(response?.data?.destinos || {});
      setOtpCode('');
      startResendCooldown(response?.data?.cooldownSeconds);
      toast.success('Novo código enviado.');
    } catch (err) {
      const status = err?.response?.status;
      const reason = err?.response?.data?.reason;
      const retryAfter = err?.response?.data?.retryAfterSeconds;

      if (status === 429 && reason === 'cooldown') {
        if (retryAfter) startResendCooldown(retryAfter);
        toast.warn(getApiErrorMessage(err, 'Aguarde para reenviar o código.'));
      } else if (status === 429) {
        toast.error(getApiErrorMessage(err, 'Limite de envios atingido.'));
      } else {
        toast.error(getApiErrorMessage(err, 'Erro ao reenviar o código.'));
      }
      console.error('Erro ao reenviar código:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatCurrency = (value) => {
    if (!value || isNaN(parseFloat(value))) return 'R$ 0,00';
    return formatters.formatCurrency(parseFloat(value));
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    try {
      const ddmmyyyy = /^\d{2}\/\d{2}\/\d{4}$/;
      if (ddmmyyyy.test(date)) {
        return date;
      }
      // Se for formato ISO (YYYY-MM-DD), usar o formatador da API que já trata timezone
      if (typeof date === 'string' && date.includes('-')) {
        return formatters.formatDate(date);
      }
      const parsedDate = new Date(date);
      if (isNaN(parsedDate.getTime())) {
        return 'N/A';
      }
      return formatters.formatDate(parsedDate);
    } catch (error) {
      console.warn('Erro ao formatar data:', date, error);
      return 'N/A';
    }
  };

  const getStatusColor = (status) => {
    const key = String(status ?? '')
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');

    if (key === 'atrasado' || key === 'em atraso' || key === 'inativo') return '#ef4444';
    if (key === 'pendente') return '#f59e0b';
    if (
      !key ||
      key === 'ativo' ||
      key === 'em dia' ||
      key === 'adimplente' ||
      key.includes('pre cadastro') ||
      key.includes('precadastro')
    ) {
      return '#10b981';
    }
    return '#64748b';
  };

  const statusContratoLabel = formatSituacaoContrato(
    resolveSituacaoRaw(contrato, selectedContrato, contratos)
  );

  const planConfig = formatters.getPlanConfig(contrato?.idPlano || cliente?.idPlano || '726');

  const pushPermission = getNotificationPermission();
  const pushState = pushEnabled
    ? 'enabled'
    : pushPermission === 'denied'
      ? 'blocked'
      : 'disabled';
  const pushVisual = {
    enabled: {
      badge: 'Ativa neste dispositivo',
      badgeBg: '#ecfdf5',
      badgeColor: '#047857',
      badgeBorder: '#a7f3d0',
      description: 'Este navegador recebe avisos do portal.',
    },
    blocked: {
      badge: 'Bloqueadas pelo navegador',
      badgeBg: '#fef2f2',
      badgeColor: '#b91c1c',
      badgeBorder: '#fecaca',
      description: 'O navegador bloqueou as notificações. Libere a permissão nas configurações do site para ativar.',
    },
    disabled: {
      badge: 'Desativadas neste dispositivo',
      badgeBg: '#f1f5f9',
      badgeColor: '#475569',
      badgeBorder: '#e2e8f0',
      description: 'Ative para receber avisos neste navegador sobre lembretes e novidades do portal.',
    },
  }[pushState];

  if (authLoading || isLoading) return <LoadingSpinner />;

  if (error) {
    return (
      <ProfileContainer>
        <ErroLoadData />
      </ProfileContainer>
    );
  }

  if (!data) {
    return (
      <ProfileContainer>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <LuTriangleAlert style={{ fontSize: '48px', color: '#ef4444', marginBottom: '16px' }} />
          <h2>Dados não encontrados</h2>
          <p>Não foram encontradas informações para o seu perfil.</p>
        </div>
      </ProfileContainer>
    );
  }

  return (
    <ProfileContainer>
      <ProfileHeader>
        <motion.h1 initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          Meu Perfil
        </motion.h1>
        <motion.p initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }}>
          Gerencie suas informações pessoais e dados do contrato
        </motion.p>
      </ProfileHeader>

      <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.3 }}>
        <StatusSection>
          <StatusCard>
            <StatusIcon 
              color={getStatusColor(statusContratoLabel)}
              bgColor={isSituacaoEmDia(statusContratoLabel) ? '#ecfdf5' : '#fef2f2'}
            > 
              <LuShieldCheck /> 
            </StatusIcon>
            <StatusContent>
              <StatusTitle>Status do Contrato</StatusTitle>
              <StatusValue>{statusContratoLabel}</StatusValue>
            </StatusContent>
          </StatusCard>

          <StatusCard>
            <StatusIcon color="#10b981" bgColor="#ecfdf5"> <LuCircleCheck /> </StatusIcon>
            <StatusContent>
              <StatusTitle>Boletos Pagos</StatusTitle>
              <StatusValue>{resumo?.pagamentosPagos || 0} / {resumo?.totalBoletos || 0}</StatusValue>
            </StatusContent>
          </StatusCard>

          <StatusCard>
            <StatusIcon color="#f59e0b" bgColor="#fffbeb"> <LuCalendar /> </StatusIcon>
            <StatusContent>
              <StatusTitle>Dia de Faturamento</StatusTitle>
              <StatusValue>{cliente?.diaFaturamento || 'N/A'}</StatusValue>
            </StatusContent>
          </StatusCard>
        </StatusSection>
      </motion.div>

      <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.4 }}>
        <ProfileGrid>
          <ProfileCard>
            <CardHeader>
              <CardTitle> <LuUser /> Informações Pessoais </CardTitle>
            </CardHeader>
            <InfoGrid>
              <InfoItem style={{ gridColumn: '1 / -1' }}>
                <InfoLabel>Nome Completo</InfoLabel>
                <InfoValue>{perfilNome || 'N/A'}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Sexo</InfoLabel>
                <InfoValue>{perfilSexo === 'M' ? 'Masculino' : perfilSexo === 'F' ? 'Feminino' : 'N/A'}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Tipo de Pessoa</InfoLabel>
                <InfoValue>{cliente?.pessoa || 'N/A'}</InfoValue>
              </InfoItem>
            </InfoGrid>
          </ProfileCard>

          <ProfileCard>
            <CardHeader>
              <CardTitle> <LuPhone /> Informações de Contato </CardTitle>
              <EditButton onClick={openEditModal}> <LuPencil size={14} /> Solicitar Alteração </EditButton>
            </CardHeader>
            <InfoGrid>
              <InfoItem>
                <InfoLabel>Telefone</InfoLabel>
                <InfoValue>{formatters.formatPhone(perfilTelefone) || 'Não informado'}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Email</InfoLabel>
                <InfoValue>{perfilEmail || 'Não informado'}</InfoValue>
              </InfoItem>
              <InfoItem style={{ gridColumn: '1 / -1' }}>
                <InfoLabel>Endereço</InfoLabel>
                <InfoValue>{perfilEndereco || 'N/A'}</InfoValue>
              </InfoItem>
              <InfoItem style={{ gridColumn: '1 / -1' }}>
                <InfoLabel>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <LuBell size={12} /> Lembretes de vencimento
                  </span>
                </InfoLabel>
                <InfoValue>
                  {perfilAceitaNotificacao
                    ? 'Sim — recebe lembretes próximos ao vencimento'
                    : 'Não — não recebe lembretes de vencimento'}
                </InfoValue>
              </InfoItem>
            </InfoGrid>
          </ProfileCard>

          {isWebPushConfigured() && (
            <PushCard>
              <CardHeader>
                <CardTitle><LuBell /> Notificações do navegador</CardTitle>
                <PushStatusBadge
                  $bg={pushVisual.badgeBg}
                  $color={pushVisual.badgeColor}
                  $border={pushVisual.badgeBorder}
                >
                  {pushVisual.badge}
                </PushStatusBadge>
              </CardHeader>
              <PushBody>
                <PushInfo>
                  <PushCopy>
                    <PushDescription>{pushVisual.description}</PushDescription>
                    {pushState === 'blocked' && (
                      <PushHelp>
                        Clique no cadeado ao lado da URL → Permissões → Notificações → Permitir.
                      </PushHelp>
                    )}
                  </PushCopy>
                </PushInfo>
                <PushActions>
                  {pushState === 'enabled' ? (
                    <PushSecondaryButton
                      type="button"
                      onClick={handleDisableWebPush}
                      disabled={pushDisabling || pushEnabling}
                    >
                      {pushDisabling ? <LuLoaderCircle className="spinner" size={14} /> : <LuBellOff size={14} />}
                      {pushDisabling ? ' Desativando…' : ' Desativar neste dispositivo'}
                    </PushSecondaryButton>
                  ) : (
                    <PushPrimaryButton
                      type="button"
                      onClick={handleEnableWebPush}
                      disabled={pushEnabling || pushDisabling || pushState === 'blocked'}
                    >
                      {pushEnabling ? <LuLoaderCircle className="spinner" size={14} /> : <LuBellRing size={14} />}
                      {pushEnabling ? ' Ativando…' : ' Ativar notificações'}
                    </PushPrimaryButton>
                  )}
                </PushActions>
              </PushBody>
            </PushCard>
          )}

          {dependentes.length > 0 && false && (
            <DependentsListContainer>
              <CardHeader>
                <CardTitle><LuUsers /> Dependentes Ativos</CardTitle>
              </CardHeader>
              {dependentes
                .filter(dep => dep.status === 'ATIVO')
                .map(dep => (
                  <DependentItem key={dep.id}>
                    <DependentName>{dep.nome}</DependentName>
                    <ActionButton
                      onClick={() => handleGenerateSingleCard({ nome: dep.nome })}
                      disabled={generatingId !== null}
                      title={`Gerar cartão para ${dep.nome}`}
                    >
                      {generatingId === dep.nome ? <LuLoaderCircle className="spinner" /> : <LuIdCard />}
                    </ActionButton>
                  </DependentItem>
                ))
              }
            </DependentsListContainer>
          )}
        </ProfileGrid>

        <Modal
          isOpen={isModalOpen}
          onClose={closeModal}
          closeOnOverlayClick={modalStep !== 'codigo'}
          showCloseButton={modalStep !== 'codigo'}
        >
          <ModalTitle>
            {modalStep === 'codigo' ? 'Verificação de Segurança' : 'Solicitar Alteração de Dados'}
          </ModalTitle>
          <ModalBody>
            {modalStep === 'form' ? (
              <>
                <p style={{ textAlign: 'left', marginTop: '-10px', marginBottom: '20px' }}>
                  Preencha os campos que deseja alterar.
                </p>
                <FormGrid>
                  <FormGroup>
                    <InfoLabel htmlFor="telefone">Telefone</InfoLabel>
                    <InputMask
                      id="telefone"
                      name="telefone"
                      mask="(99) 99999-9999"
                      value={formData.telefone || ''}
                      onChange={handleFormChange}
                    >
                      {(inputProps) => <EditableInput {...inputProps} type="tel" />}
                    </InputMask>
                    {formErrors.telefone && <InlineErrorMessage>{formErrors.telefone}</InlineErrorMessage>}
                  </FormGroup>
                  <FormGroup>
                    <InfoLabel htmlFor="email">Email</InfoLabel>
                    <EditableInput
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email || ''}
                      onChange={handleFormChange}
                    />
                    {formErrors.email && <InlineErrorMessage>{formErrors.email}</InlineErrorMessage>}
                  </FormGroup>
                </FormGrid>
                <ContactHelperText>
                  O código de confirmação chegará através desses meios de contato.
                </ContactHelperText>
                <FormSectionDivider>Endereço</FormSectionDivider>
                <FormGroup>
                  <InfoLabel htmlFor="cep">CEP</InfoLabel>
                  <InputMask
                    id="cep"
                    name="cep"
                    mask="99999-999"
                    value={formData.cep || ''}
                    onChange={handleFormChange}
                    disabled={isCepLoading}
                  >
                    {(inputProps) => <EditableInput {...inputProps} placeholder="00000-000" />}
                  </InputMask>
                  {isCepLoading
                    ? <FieldHelperText>Buscando endereço...</FieldHelperText>
                    : <FieldHelperText>Ao informar o CEP, cidade e estado são preenchidos automaticamente.</FieldHelperText>
                  }
                </FormGroup>
                <FormGroup>
                  <InfoLabel htmlFor="endereco">Logradouro</InfoLabel>
                  <EditableInput id="endereco" name="endereco" value={formData.endereco || ''} onChange={handleFormChange} disabled={isCepLoading} />
                </FormGroup>
                <FormGrid>
                  <FormGroup>
                    <InfoLabel htmlFor="numero">Número</InfoLabel>
                    <EditableInput
                      id="numero"
                      name="numero"
                      value={formData.semNumero ? 'S/N' : (formData.numero || '')}
                      onChange={handleFormChange}
                      disabled={!!formData.semNumero}
                      placeholder="Ex: 235"
                    />
                    <CheckboxRow>
                      <input
                        type="checkbox"
                        checked={!!formData.semNumero}
                        onChange={handleSemNumeroChange}
                      />
                      Sem número
                    </CheckboxRow>
                  </FormGroup>
                  <FormGroup>
                    <InfoLabel htmlFor="complemento">Complemento</InfoLabel>
                    <EditableInput id="complemento" name="complemento" value={formData.complemento || ''} onChange={handleFormChange} />
                  </FormGroup>
                </FormGrid>
                <FormGroup>
                  <InfoLabel htmlFor="referencia">Referência</InfoLabel>
                  <EditableInput
                    id="referencia"
                    name="referencia"
                    value={formData.referencia || ''}
                    onChange={handleFormChange}
                    placeholder="Ex.: próximo ao mercado, em frente à praça"
                  />
                </FormGroup>
                <FormGroup>
                  <InfoLabel htmlFor="bairro">Bairro</InfoLabel>
                  <EditableInput id="bairro" name="bairro" value={formData.bairro || ''} onChange={handleFormChange} disabled={isCepLoading} />
                </FormGroup>
                <FormGrid>
                  <FormGroup>
                    <InfoLabel>
                      Cidade
                      <FieldHint
                        tabIndex={0}
                        data-tooltip="Preenchido automaticamente pelo CEP. Não é possível editar manualmente."
                        aria-label="Cidade preenchida pelo CEP"
                      >
                        <LuCircleHelp size={14} />
                      </FieldHint>
                    </InfoLabel>
                    <ReadonlyValue>{formData.cidade || 'Preenchido pelo CEP'}</ReadonlyValue>
                  </FormGroup>
                  <FormGroup>
                    <InfoLabel>
                      Estado
                      <FieldHint
                        tabIndex={0}
                        data-tooltip="Preenchido automaticamente pelo CEP. Não é possível editar manualmente."
                        aria-label="Estado preenchido pelo CEP"
                      >
                        <LuCircleHelp size={14} />
                      </FieldHint>
                    </InfoLabel>
                    <ReadonlyValue>{formatEstadoLabel(formData.estado)}</ReadonlyValue>
                  </FormGroup>
                </FormGrid>
                <FormSectionDivider>Notificações</FormSectionDivider>
                <NotificationOptIn>
                  <input
                    type="checkbox"
                    checked={!!formData.aceita_notificacao}
                    onChange={handleAceitaNotificacaoChange}
                  />
                  <span>
                    Aceito receber lembretes próximos ao vencimento
                    <small>Você será avisado antes da data de pagamento da mensalidade.</small>
                  </span>
                </NotificationOptIn>
              </>
            ) : (
              <>
                <OtpHintBox>
                  Enviamos um código de 6 dígitos para
                  {otpDestinos?.emailMascarado ? <> o e-mail <strong>{otpDestinos.emailMascarado}</strong></> : null}
                  {otpDestinos?.emailMascarado && otpDestinos?.telefoneMascarado ? ' e' : null}
                  {otpDestinos?.telefoneMascarado ? <> o telefone <strong>{otpDestinos.telefoneMascarado}</strong></> : null}
                  {!otpDestinos?.emailMascarado && !otpDestinos?.telefoneMascarado ? ' os contatos informados' : null}.
                  <br />
                  O código é válido por 10 minutos.
                </OtpHintBox>
                <FormGroup>
                  <InfoLabel htmlFor="otpCode">Código de verificação</InfoLabel>
                  <OtpInput
                    id="otpCode"
                    name="otpCode"
                    inputMode="numeric"
                    maxLength={6}
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    autoComplete="one-time-code"
                  />
                </FormGroup>
                <div style={{ textAlign: 'center' }}>
                  <LinkButton
                    type="button"
                    onClick={handleResendCode}
                    disabled={isSubmitting || resendCooldown > 0}
                  >
                    Reenviar código
                    {resendCooldown > 0 && (
                      <CooldownBadge>{resendCooldown}s</CooldownBadge>
                    )}
                  </LinkButton>
                </div>
              </>
            )}
          </ModalBody>
          <ModalFooter>
            {modalStep === 'form' ? (
              <>
                <ModalButton className="cancel" onClick={closeModal} disabled={isSubmitting}> Cancelar </ModalButton>
                <ModalButton className="confirm" onClick={handleSubmitRequest} disabled={isSubmitting}>
                  {isSubmitting ? 'Enviando...' : 'Enviar Solicitação'}
                </ModalButton>
              </>
            ) : (
              <>
                <ModalButton className="cancel" onClick={() => setModalStep('form')} disabled={isSubmitting}>
                  Voltar
                </ModalButton>
                <ModalButton className="confirm" onClick={handleConfirmCode} disabled={isSubmitting || otpCode.length !== 6}>
                  {isSubmitting ? 'Validando...' : 'Validar Código'}
                </ModalButton>
              </>
            )}
          </ModalFooter>
        </Modal>
      </motion.div>

      <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.5 }}>
        <ContractSection>
          {/* 
            -- Desativado temporariamente para não confundir o usuário --
            <ContractStatusWrapper>
            <span style={{
              display: 'inline-block', padding: '6px 12px', borderRadius: '8px', fontSize: '11px',
              fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.5px',
              backgroundColor: isSituacaoEmDia(statusContratoLabel) ? '#dcfce7' : '#fef2f2',
              color: isSituacaoEmDia(statusContratoLabel) ? '#166534' : '#991b1b',
              border: `1px solid ${isSituacaoEmDia(statusContratoLabel) ? '#bbf7d0' : '#fecaca'}`
            }}>
              {statusContratoLabel}
            </span>
          </ContractStatusWrapper> */}

          <ContractHeader>
            <ContractAvatar>
              <LuFileText />
            </ContractAvatar>
            <ContractTitle>
              <h3>Informações do Contrato</h3>
              <span>Dados técnicos e vigência</span>
            </ContractTitle>
          </ContractHeader>

          <ContractInfoGrid>
            <ContractItem>
              <ContractLabel>Código do Contrato</ContractLabel>
              <ContractValue>#{contrato?.numero || 'N/A'}</ContractValue>
            </ContractItem>
            
            <ContractItem>
              <ContractLabel>Início do Contrato</ContractLabel>
              <ContractValue>{formatDate(contrato?.inicioVigencia)}</ContractValue>
            </ContractItem>
            
            <ContractItem>
              <ContractLabel>Plano</ContractLabel>
              <ContractValue>{contrato?.plano || 'N/A'}</ContractValue>
            </ContractItem>
            
            <ContractItem>
              <ContractLabel>Próximo Vencimento</ContractLabel>
              <ContractValue>{formatDate(contrato?.proximoVencimento)}</ContractValue>
            </ContractItem>
          </ContractInfoGrid>
        </ContractSection>

        <CardGenerator
          membros={todosMembros}
          contrato={contrato?.numero}
        />
      </motion.div>
    </ProfileContainer>
  );
};

export default Profile;
